// Global Instructions Rule Applied!
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Row, Col, Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { DealCommitments } from '../../../../../core/components/DealCommitments/index.jsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../../core/components/AccountSourcingFields.tsx'
import { useCurrencies } from '../../../../currencies/useCurrencies.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/index.ts'
import {
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_DEAL_TYPE
} from '../../../../../state_manager/constants/lookupCategories.ts'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import {
  getOverviewDealInfoFields,
  getOverviewRoundInfoFields,
  getDealTeamFields
} from '../../../utils/fieldConfigs.ts'
import { useDealManager } from '../../../hooks/useDealManager.ts'
import { useCommitDealGate } from '../../../hooks/useCommitDealGate.tsx'
import { useOriginationExcludedDealType } from '../../../hooks/useOriginationExcludedDealType.ts'
import { fetchDealById } from '../../../services/dealsService.ts'
import { useStageMoveRights } from '../../../../../core/hooks/useStageMoveRights.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

interface DealOverviewSectionProps {
  deal?: any
  formData?: any
  handleFieldChange?: (key: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  commitments?: any[]
  handleAddCommitment?: () => void
  handleRemoveCommitment?: (id: string) => void
  handleCommitmentChange?: (commitments: any[]) => void
  handleSaveCommitmentField?: (id: string, field: string, value: any) => void
  selectedContacts?: {
    introduced_by?: any[]
    sourced_by?: any[]
    deal_team_lead?: any[]
  }
  onContactsChange?: (contacts: { introduced_by?: any[]; sourced_by?: any[]; deal_team_lead?: any[] }) => void
  showTitle?: boolean
  cardProps?: any
  isReadOnly?: boolean
  isDrawer?: boolean
  showCurrency?: boolean
  recordId?: string | number | null
}

export function DealOverviewSection({
  deal,
  formData,
  handleFieldChange,
  onPatchSuccess,
  commitments = [],
  handleCommitmentChange,
  selectedContacts = { introduced_by: [], sourced_by: [], deal_team_lead: [] },
  onContactsChange,
  cardProps = {},
  isReadOnly = false,
  isDrawer = false,
  showCurrency = true,
  recordId = null
}: DealOverviewSectionProps) {
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()
  const { getLookupOptions, detailsByCategory } = useLookups()
  const { defaultPipelineId } = useDealManager()
  const { gate: commitGate, holder: commitGateHolder } = useCommitDealGate()
  const isOriginationExcluded = useOriginationExcludedDealType()

  const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])

  const fundingStageOptions = getLookupOptions(LOOKUP_CATEGORY_FUNDING_STAGE)
  const dealTypeOptions = getLookupOptions(LOOKUP_CATEGORY_DEAL_TYPE)

  // React 19 + AntD: static Modal.confirm renders nothing — use the useModal instance
  const [modal, modalContextHolder] = Modal.useModal()

  const dispatch = useDispatch()

  const companyLookupId =
    detailsByCategory?.['Global Configs']?.find((option: any) => option.label === 'internal_company_id')?.value || null

  const [previousContacts, setPreviousContacts] = useState(() => {
    return {
      introduced_by: [...(selectedContacts.introduced_by || [])],
      sourced_by: [...(selectedContacts.sourced_by || [])],
      deal_team_lead: [...(selectedContacts.deal_team_lead || [])]
    }
  })

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    const hasSelectedData =
      (selectedContacts.introduced_by?.length ?? 0) > 0 ||
      (selectedContacts.sourced_by?.length ?? 0) > 0 ||
      (selectedContacts.deal_team_lead?.length ?? 0) > 0
    const hasPreviousData =
      previousContacts.introduced_by?.length > 0 ||
      previousContacts.sourced_by?.length > 0 ||
      previousContacts.deal_team_lead?.length > 0

    if ((hasSelectedData && !hasPreviousData) || (!hasInitializedRef.current && hasSelectedData)) {
      setPreviousContacts({
        introduced_by: [...(selectedContacts.introduced_by || [])],
        sourced_by: [...(selectedContacts.sourced_by || [])],
        deal_team_lead: [...(selectedContacts.deal_team_lead || [])]
      })
      hasInitializedRef.current = true
    }
  }, [
    selectedContacts.introduced_by?.length,
    selectedContacts.sourced_by?.length,
    selectedContacts.deal_team_lead?.length
  ])

  const data = deal || formData || {}

  const dealId = recordId || data[DealFields.deals__id] || deal?.[DealFields.deals__id] || deal?.id
  const roundId = data[DealFields.rounds__id] || data.round || null

  const dealPipelineId = data[DealFields.deals__pipeline_origination] || defaultPipelineId

  // Stages the user may not move this deal into - or every other stage, when
  // they may not move it out of its current one - are disabled with the reason.
  const applyStageMoveRights = useStageMoveRights()
  const currentStageId = data[DealFields.pipeline_deals__stage]

  const pipelineStageOptions = useMemo(() => {
    if (!dealPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
      return []
    }

    const filteredConfigs = pipelineConfigs.filter(
      (config: any) => config.pipeline === dealPipelineId && config.visible_tbl === true
    )

    const allPipelineStageOptions = filteredConfigs
      .map((config: any) => {
        const stage = pipelineStages.find((s: any) => s.id === config.stage)
        return {
          value: config.id,
          label: stage?.name || '',
          sequence: config.sequence_tbl || config.sequence_kb || 0
        }
      })
      .filter((option: any) => option.label)
      .sort((a: any, b: any) => a.label.localeCompare(b.label))

    return applyStageMoveRights(allPipelineStageOptions, currentStageId)
  }, [dealPipelineId, pipelineConfigs, pipelineStages, applyStageMoveRights, currentStageId])

  const shouldShowCurrency = showCurrency && !isDrawer

  // Warn when changing the type of a deal that already sits in an execution pipeline —
  // the change does NOT move the deal (deal_execution__enter skips deals already in execution).
  const confirmDealTypeChange = useCallback(
    (newValue: any, currentRecord: Record<string, any>) => {
      const execPipelineId =
        data[DealFields.deals__pipeline_execution] || currentRecord?.[DealFields.deals__pipeline_execution]
      if (!execPipelineId) return true
      const pipelineName = pipelines.find((pipeline: any) => pipeline.id === execPipelineId)?.name || 'its current'
      return new Promise<boolean>((resolve) => {
        modal.confirm({
          title: 'Deal already in execution',
          content: `This deal is already in the ${pipelineName} execution pipeline — changing the type will not move it.`,
          okText: 'Change type',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
    },
    [data, pipelines, modal]
  )

  // InputManager hands confirmPatch the section's field values only, so merge
  // the record in: the gate reads commitments and round fields that are not
  // fields of this InputManager.
  const confirmStageChange = useCallback(
    (newValue: any, currentRecord: Record<string, any>) => commitGate({ ...data, ...currentRecord }, newValue),
    [commitGate, data]
  )

  const fieldOptions = {
    pipelineStageOptions,
    fundingStageOptions,
    dealTypeOptions,
    confirmDealTypeChange,
    confirmStageChange,
    isOriginationExcluded,
    getLookupOptions,
    currencyOptions: getCurrencyOptions(),
    getCurrencyById
  }

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    if (updates[DealFields.pipeline_deals__stage]) {
      const pipelineConfigId = updates[DealFields.pipeline_deals__stage]
      updates[DealFields.pipeline_config__id] = pipelineConfigId
      const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
      if (config) {
        const stage = pipelineStages.find((s: any) => s.id === config.stage)
        if (stage && stage.name) {
          updates[DealFields.pipeline_deals__stage__label] = stage.name
        }
      }
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (isDrawer && patchRecordId === dealId && dealId && currentToken) {
      try {
        const response = await fetchDealById(dealId, currentToken)

        if (response.data && !response.error) {
          dispatch(updateDealRecord({ dealId: dealId, updates: response.data }))

          const fetchedDeal = response.data
          const updatedContacts = {
            introduced_by: fetchedDeal[DealFields.contact_accounts__introduced_by] || [],
            sourced_by: fetchedDeal[DealFields.contact_accounts__sourced_by] || [],
            deal_team_lead: fetchedDeal[DealFields.contact_deals__lead] || []
          }
          setPreviousContacts(updatedContacts)
        }
      } catch (error) {
        console.error('Error fetching deal after patch:', error)
      }
    }

    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(updateDealRecord({ dealId: patchRecordId, updates }))
    }
  }

  return (
    <FormSection
      data-debug='deal-overview-section'
      data-component='DealOverviewSection'
      data-is-drawer={isDrawer}
      data-show-currency={shouldShowCurrency}
      title='Deal Overview'
      titleColor='blue'
      className='shadow-sm border border-gray-200'
      {...cardProps}
    >
      {modalContextHolder}
      {commitGateHolder}
      <div data-debug='deal-overview-content'>
        {isDrawer ? (
          <div>
            <Row data-debug='deal-overview-drawer-layout' gutter={16}>
              <Col data-debug='deal-overview-left-column' xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={getOverviewDealInfoFields(data, fieldOptions, isReadOnly, true)}
                  onChange={(key: string, value: any) => handleFieldChange?.(key, value)}
                  enableAutoPatch={!isReadOnly && !!dealId}
                  patchId={dealId}
                  recordId={dealId}
                  onPatchSuccess={handlePatchSuccessLocal}
                  className='deal-overview-deal-info'
                  fieldWrapping={true}
                />
                {/* Deal Team stays directly under Pipeline Stage: it used to be a
                    full-width row below both columns and only read as part of
                    this column because this column was the taller one. */}
                {!isReadOnly && (
                  <>
                    {/* Introduced by / Sourced by belong to the deal's company:
                        shown read-only here, changed on the company form. */}
                    <AccountSourcingFields
                      teamFields={getDealTeamFields(data, selectedContacts, companyLookupId, previousContacts)}
                      account={data?.[DealFields.deals__account]}
                      accountNoun='company'
                      inputMode='read'
                      labelPosition='inline'
                      className='deal-overview-deal-team-sourcing'
                      fieldWrapping={true}
                    />
                    <InputManager
                      inputMode='read'
                      labelPosition='inline'
                      fields={getDealTeamFields(data, selectedContacts, companyLookupId, previousContacts).filter(
                        (field) => !isSourcingField(field)
                      )}
                      onChange={(key: string, value: any) => {
                        if (key === DealFields.contact_deals__lead) {
                          onContactsChange?.({ ...selectedContacts, deal_team_lead: value })
                        }
                      }}
                      enableAutoPatch={!!dealId}
                      patchId={dealId}
                      recordId={dealId}
                      onPatchSuccess={handlePatchSuccessLocal}
                      className='deal-overview-deal-team'
                      fieldWrapping={true}
                    />
                  </>
                )}
              </Col>

              <Col data-debug='deal-overview-right-column' xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={getOverviewRoundInfoFields(data, fieldOptions, false, isReadOnly, true)}
                  onChange={(key: string, value: any) => handleFieldChange?.(key, value)}
                  enableAutoPatch={!isReadOnly && !!roundId}
                  patchId={roundId}
                  recordId={roundId}
                  onPatchSuccess={handlePatchSuccessLocal}
                  className='deal-overview-round-info'
                  fieldWrapping={true}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <div data-debug='deal-overview-single-column' className='deal-overview-fields'>
            <InputManager
              inputMode='read'
              labelPosition='inline'
              fields={[
                ...getOverviewDealInfoFields(data, fieldOptions, isReadOnly),
                ...getOverviewRoundInfoFields(data, fieldOptions, shouldShowCurrency, isReadOnly)
              ]}
              onChange={(key: string, value: any) => handleFieldChange?.(key, value)}
              enableAutoPatch={!isReadOnly && !!(dealId || roundId)}
              patchId={dealId}
              recordId={dealId}
              onPatchSuccess={handlePatchSuccessLocal}
              className='deal-overview-single-column'
              fieldWrapping={true}
            />
          </div>
        )}

        {!isReadOnly && commitments && (
          <DealCommitments
            mode='existing'
            initialCommitments={commitments}
            onChange={handleCommitmentChange}
            currencySymbol={getCurrencyById(data[DealFields.deals__currency])?.symbol || '$'}
            showTotal={true}
            recordId={dealId}
          />
        )}
      </div>
    </FormSection>
  )
}
